// sessionRecapImage.js - Build a shareable PNG recap for an individual session.
import { API_BASE } from '../config/api';
import { authService } from '../services/auth';
import logger from './logger';

const CANVAS_WIDTH = 1200;
const CANVAS_HEIGHT = 630;

function loadImage(url) {
  return new Promise((resolve) => {
    if (!url) {
      resolve(null);
      return;
    }

    const isApiUrl = url.startsWith(API_BASE);
    const token = isApiUrl ? authService.getToken() : null;
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
    const credentials = isApiUrl ? 'include' : 'omit';
    const timeout = setTimeout(() => resolve(null), 5000);

    // Fetch the game image with the user's auth token (for API thumbnails only)
    // and load it from a same-origin object URL. This avoids CORS/taint issues
    // that prevent the canvas from being exported when an <img> is loaded cross-origin.
    fetch(url, { headers: authHeaders, credentials })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Image fetch failed: ${res.status}`);
        }
        return res.blob();
      })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
          clearTimeout(timeout);
          URL.revokeObjectURL(objectUrl);
          resolve(img);
        };
        img.onerror = () => {
          clearTimeout(timeout);
          URL.revokeObjectURL(objectUrl);
          resolve(null);
        };
        img.src = objectUrl;
      })
      .catch((err) => {
        logger.warn('Failed to load session cover:', err);
        clearTimeout(timeout);
        resolve(null);
      });
  });
}

function wrapText(ctx, text, maxWidth) {
  const words = text.split(' ');
  const lines = [];
  let currentLine = words[0] || '';
  for (let i = 1; i < words.length; i += 1) {
    const word = words[i];
    const width = ctx.measureText(`${currentLine} ${word}`).width;
    if (width < maxWidth) {
      currentLine += ` ${word}`;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  if (currentLine) lines.push(currentLine);
  return lines;
}

export async function buildSessionRecapImage(session, options = {}) {
  const {
    filename = 'session-recap.png',
    backgroundColor = '#fafbfc',
  } = options;

  const canvas = document.createElement('canvas');
  canvas.width = CANVAS_WIDTH;
  canvas.height = CANVAS_HEIGHT;
  const ctx = canvas.getContext('2d');

  // Background
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

  // Brand header bar
  ctx.fillStyle = '#3f51b5';
  ctx.fillRect(0, 0, CANVAS_WIDTH, 16);

  // Card
  const margin = 70;
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.roundRect(margin, 90, CANVAS_WIDTH - margin * 2, CANVAS_HEIGHT - 180, 24);
  ctx.fill();

  // Game cover - use the backend thumbnail proxy so the same image works
  // in the browser without CORS/mixed-content issues.
  const coverSize = 360;
  const coverX = margin + 40;
  const coverY = 140;
  const coverUrl = session.game_id ? `${API_BASE}/games/${session.game_id}/thumbnail` : null;
  const cover = await loadImage(coverUrl);
  if (cover) {
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(coverX, coverY, coverSize, coverSize, 20);
    ctx.clip();
    const scale = Math.max(coverSize / cover.width, coverSize / cover.height);
    const drawW = cover.width * scale;
    const drawH = cover.height * scale;
    ctx.drawImage(cover, coverX + (coverSize - drawW) / 2, coverY + (coverSize - drawH) / 2, drawW, drawH);
    ctx.restore();
  } else {
    ctx.fillStyle = '#e0e0e0';
    ctx.beginPath();
    ctx.roundRect(coverX, coverY, coverSize, coverSize, 20);
    ctx.fill();
    ctx.fillStyle = '#9e9e9e';
    ctx.font = '96px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('🎲', coverX + coverSize / 2, coverY + coverSize / 2 + 30);
    ctx.textAlign = 'left';
  }

  // Text area
  const textX = coverX + coverSize + 55;
  const textMaxWidth = CANVAS_WIDTH - textX - margin - 40;
  let textY = coverY + 20;

  // Game title
  ctx.fillStyle = '#212529';
  ctx.font = 'bold 54px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  const titleLines = wrapText(ctx, session.game_name || 'Unknown Game', textMaxWidth);
  titleLines.slice(0, 2).forEach((line) => {
    ctx.fillText(line, textX, textY);
    textY += 66;
  });

  // Date
  if (session.date_display || session.chosen_at) {
    textY += 10;
    ctx.fillStyle = '#6c757d';
    ctx.font = '30px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    const dateStr = session.date_display || new Date(session.chosen_at).toLocaleDateString();
    ctx.fillText(`Played on ${dateStr}`, textX, textY);
    textY += 50;
  }

  // Players
  const participants = Array.isArray(session.participants) ? session.participants : [];
  const winnerIds = new Set(Array.isArray(session.winners) ? session.winners : []);
  if (participants.length > 0) {
    ctx.fillStyle = '#212529';
    ctx.font = 'bold 30px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    ctx.fillText('Players', textX, textY);
    textY += 42;

    ctx.font = '26px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    ctx.fillStyle = '#212529';
    participants.slice(0, 6).forEach((p) => {
      const name = p.username || p.name || p.display_name || 'Player';
      const score = p.score;
      const isWinner = winnerIds.has(p.user_id);
      const prefix = isWinner ? '🏆 ' : '• ';
      const scorePart = score !== undefined && score !== '' ? ` — ${score}` : '';
      const line = `${prefix}${name}${scorePart}`;
      ctx.fillText(line, textX + 10, textY);
      textY += 36;
    });
  }

  // Branding footer
  ctx.fillStyle = '#6c757d';
  ctx.font = '22px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillText('Pista', margin + 40, CANVAS_HEIGHT - 50);

  ctx.textAlign = 'right';
  ctx.fillText('Shared via Pista by VoyagerBug', CANVAS_WIDTH - margin - 40, CANVAS_HEIGHT - 50);
  ctx.textAlign = 'left';

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        resolve(null);
        return;
      }
      resolve(new File([blob], filename, { type: 'image/png' }));
    }, 'image/png');
  });
}

export default buildSessionRecapImage;
